import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { Download, Plus, Trash2, X, Volume2, VolumeX } from 'lucide-react';
import { pyqAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import PageMeta from '../SEO/PageMeta';
import PublicFooter from '../PublicFooter/PublicFooter';
import skyArt from '../../assets/temple-far1.jpg';
import './PYQPage.css';

const PAPER_LABELS = {
  'GS-1': 'General Studies Paper 1',
  'GS-2': 'CSAT Paper 2 (Qualifying)',
  'GS-I':  'GS-I — History, Geography, Society',
  'GS-II': 'GS-II — Governance, Constitution, IR',
  'GS-III':'GS-III — Economy, Environment, Security',
  'GS-IV': 'GS-IV — Ethics, Integrity, Aptitude',
  'Essay': 'Essay Paper',
};

const YEARS = [2026,2025,2024,2023,2022,2021,2020,2019,2018,2017,2016,2015,2014,2013,2012,2011];
const PRELIMS_CODES = ['GS-1', 'GS-2'];
const MAINS_CODES   = ['GS-I', 'GS-II', 'GS-III', 'GS-IV', 'Essay'];

/* Book colours, picked to sit on the temple sky */
const TONES = [
  { bg: '#d3553b', fg: '#fdf6e6' },
  { bg: '#2c3f46', fg: '#f8e5c7' },
  { bg: '#f2b84b', fg: '#1b272d' },
  { bg: '#3d6b4f', fg: '#fdf6e6' },
  { bg: '#e8806a', fg: '#1b272d' },
  { bg: '#f8e5c7', fg: '#1b272d' },
];

const GHOSTS = Array.from({ length: 9 });
const EASE = 'cubic-bezier(.22, .8, .24, 1)';
const calm = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function PYQPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [papers,   setPapers]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [sel,      setSel]      = useState(null);   // year of the open book
  const [phase,    setPhase]    = useState('');     // '' | 'moving' | 'open' | 'closing'
  const [fired,    setFired]    = useState(0);      // how many links have been shot
  const [settled,  setSettled]  = useState(false);  // all links landed
  const [muted,    setMuted]    = useState(false);
  const [intro,    setIntro]    = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');
  const [toast,    setToast]    = useState('');
  const [form,     setForm]     = useState({
    year: '2025', examType: 'Prelims', paperCode: 'GS-1', paperTitle: '', pdfUrl: '', notes: '',
  });

  const stageRef = useRef(null);
  const trackRef = useRef(null);
  const closeRef = useRef(null);
  const bookRefs = useRef({});
  const chipRefs = useRef([]);
  const flipRef  = useRef(null);
  const audio    = useRef(null);
  const timers   = useRef([]);
  const shotT    = useRef([]);
  const pan      = useRef({ cur: 0, target: 0, raf: 0 });
  const selR     = useRef(null);
  const phaseR   = useRef('');
  const mutedR   = useRef(false);
  const firedR   = useRef(0);
  selR.current = sel; phaseR.current = phase; mutedR.current = muted;

  const later = (fn, ms) => { const id = window.setTimeout(fn, ms); timers.current.push(id); return id; };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pyqAPI.list({});
      setPapers(res.data.papers || []);
    } catch { /* leave the shelf empty */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = window.setTimeout(() => setIntro(false), 1800); return () => clearTimeout(id); }, []);
  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    shotT.current.forEach(clearTimeout);
    cancelAnimationFrame(pan.current.raf);
    audio.current?.close?.();
  }, []);

  // Oldest on the left, newest on the right
  const { years, byYear } = useMemo(() => {
    const by = {};
    papers.forEach(p => {
      if (!by[p.year]) by[p.year] = { Prelims: [], Mains: [] };
      if (!by[p.year][p.examType]) by[p.year][p.examType] = [];
      by[p.year][p.examType].push(p);
    });
    return { years: Object.keys(by).sort((a, b) => a - b), byYear: by };
  }, [papers]);

  const empty = loading || years.length === 0;
  const cur = sel ? byYear[sel] : null;
  const list = cur ? [...(cur.Prelims || []), ...(cur.Mains || [])] : [];
  // The open book always stands at the right end of the row
  const shown = sel ? [...years.filter(y => y !== sel), sel] : years;

  /* ── Row follows the mouse when there are more books than the screen holds ── */
  const maxPan = () => {
    const s = stageRef.current, t = trackRef.current;
    return s && t ? Math.max(0, t.offsetWidth - s.clientWidth) : 0;
  };

  const runPan = () => {
    const p = pan.current;
    if (p.raf) return;
    const tick = () => {
      const track = trackRef.current;
      if (!track) { p.raf = 0; return; }
      p.cur += (p.target - p.cur) * (calm() ? 1 : 0.12);
      if (Math.abs(p.target - p.cur) < 0.4) p.cur = p.target;
      track.style.transform = `translate3d(${p.cur}px, 0, 0)`;
      p.raf = p.cur === p.target ? 0 : requestAnimationFrame(tick);
    };
    p.raf = requestAnimationFrame(tick);
  };

  const onMove = e => {
    if (e.pointerType !== 'mouse' || selR.current) return;
    const r = stageRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    pan.current.target = -maxPan() * ratio;
    runPan();
  };

  const onBookFocus = e => {
    const s = stageRef.current;
    if (!s || !e.currentTarget.matches(':focus-visible')) return;
    const el = e.currentTarget;
    const want = el.offsetLeft - s.clientWidth / 2 + el.offsetWidth / 2;
    pan.current.target = -Math.min(maxPan(), Math.max(0, want));
    runPan();
  };

  const toEnd = () => {
    const s = stageRef.current;
    if (!s) return;
    if (window.matchMedia?.('(pointer: coarse)').matches) s.scrollTo({ left: s.scrollWidth, behavior: 'smooth' });
    else { pan.current.target = -maxPan(); runPan(); }
  };

  useEffect(() => {
    const onResize = () => {
      if (maxPan() === 0 && trackRef.current) {
        pan.current.cur = 0; pan.current.target = 0; trackRef.current.style.transform = '';
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  });

  /* ── Sound: a bowstring snap and a short whoosh, made in the browser ── */
  const prime = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      if (!audio.current) audio.current = new AC();
      if (audio.current.state === 'suspended') audio.current.resume();
    } catch { /* no sound */ }
  };

  const playShot = () => {
    const ctx = audio.current;
    if (!ctx || mutedR.current) return;
    try {
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.24);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
      const noise = ctx.createBufferSource();
      noise.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass'; bp.Q.value = 1.1;
      bp.frequency.setValueAtTime(800, t);
      bp.frequency.exponentialRampToValueAtTime(5600, t + 0.22);
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.32, t);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.24);
      noise.connect(bp).connect(ng).connect(ctx.destination);
      noise.start(t);

      const osc = ctx.createOscillator();
      const og = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(260, t);
      osc.frequency.exponentialRampToValueAtTime(70, t + 0.12);
      og.gain.setValueAtTime(0.4, t);
      og.gain.exponentialRampToValueAtTime(0.001, t + 0.14);
      osc.connect(og).connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.16);
    } catch { /* no sound */ }
  };

  /* ── Moving books: remember where they were, then slide from there ── */
  const snap = year => {
    const rects = {};
    Object.entries(bookRefs.current).forEach(([y, el]) => { if (el) rects[y] = el.getBoundingClientRect(); });
    flipRef.current = { rects, year };
  };

  useLayoutEffect(() => {
    const f = flipRef.current;
    if (!f) return;
    flipRef.current = null;
    if (calm()) return;
    Object.entries(bookRefs.current).forEach(([y, el]) => {
      const a = f.rects[y];
      if (!el || !a) return;
      const b = el.getBoundingClientRect();
      const dx = a.left - b.left, dy = a.top - b.top;
      if (!dx && !dy) return;
      const picked = y === f.year;
      const frames = picked
        ? [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: `translate(${dx * 0.5}px, -48px)`, offset: 0.5 }, { transform: 'none' }]
        : [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }];
      if (picked) el.style.zIndex = '5';
      const anim = el.animate(frames, { duration: 780, easing: EASE });
      anim.onfinish = () => { el.style.zIndex = ''; };
    });
  }, [sel]);

  /* ── Open: the book slides out to the right end, then shoots its links ── */
  const shoot = (k, year) => {
    const el = chipRefs.current[k];
    const book = bookRefs.current[year];
    setFired(k + 1); firedR.current = k + 1;
    playShot();
    if (!el || !book || calm()) return;
    const b = book.getBoundingClientRect();
    const c = el.getBoundingClientRect();
    const dx = b.right - 6 - (c.left + c.width / 2);
    const dy = b.top + b.height / 2 - (c.top + c.height / 2);
    el.classList.add('fly');
    const a = el.animate([
      { transform: `translate(${dx}px, ${dy}px) scale(.25, .5)`, opacity: 0 },
      { transform: `translate(${dx * 0.18}px, ${dy * 0.18}px) scale(1.12, .92)`, opacity: 1, offset: 0.72 },
      { transform: 'translate(0, 0) scale(1)', opacity: 1 },
    ], { duration: 560, easing: 'cubic-bezier(.16, .84, .3, 1)', fill: 'both' });
    a.onfinish = () => { el.classList.remove('fly'); a.cancel(); };
    book.animate([{ transform: 'none' }, { transform: 'translateX(-7px)' }, { transform: 'none' }], { duration: 200 });
  };

  const openBook = year => {
    const ph = phaseR.current, cs = selR.current;
    if (ph === 'moving' || ph === 'closing') return;
    if (cs) { closeBook(cs === year ? undefined : year); return; }
    prime();
    snap(year);
    setFired(0); firedR.current = 0; setSettled(false);
    setSel(year); setPhase('moving');
    later(() => setPhase('open'), calm() ? 0 : 820);
  };

  useEffect(() => {
    if (phase !== 'open' || !sel) return;
    toEnd();
    closeRef.current?.focus({ preventScroll: true });
    const year = sel, n = list.length, step = calm() ? 0 : 340;
    for (let k = 0; k < n; k++) shotT.current.push(window.setTimeout(() => shoot(k, year), 380 + k * step));
    shotT.current.push(window.setTimeout(() => { setFired(999); firedR.current = 999; setSettled(true); }, 380 + n * step + 700));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  /* ── Close: links fly back in, the book returns to its place on the shelf ── */
  const closeBook = next => {
    if (phaseR.current !== 'open') return;
    const year = selR.current;
    shotT.current.forEach(clearTimeout); shotT.current = [];
    setPhase('closing');
    const book = bookRefs.current[year];
    const b = book?.getBoundingClientRect();
    const live = chipRefs.current.filter((el, k) => el && k < firedR.current).reverse();
    const quick = calm() || !b;
    live.forEach((el, i) => {
      el.getAnimations().forEach(a => a.cancel());
      if (quick) return;
      const c = el.getBoundingClientRect();
      const dx = b.right - 6 - (c.left + c.width / 2), dy = b.top + b.height / 2 - (c.top + c.height / 2);
      el.animate([
        { transform: 'none', opacity: 1 },
        { transform: `translate(${dx}px, ${dy}px) scale(.25, .5)`, opacity: 0 },
      ], { duration: 300, delay: i * 70, easing: 'cubic-bezier(.5, 0, .8, .4)', fill: 'forwards' });
    });
    const wait = quick ? 0 : live.length * 70 + 320;
    later(() => { snap(year); setSel(null); setFired(0); firedR.current = 0; setSettled(false); }, wait);
    later(() => {
      setPhase('');
      bookRefs.current[year]?.focus?.({ preventScroll: true });
      if (next) openBook(next);
    }, wait + (calm() ? 0 : 820));
  };

  useEffect(() => {
    if (!sel) return undefined;
    const onKey = e => { if (e.key === 'Escape' && !showForm) closeBook(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  // The last paper of a year was removed: put the book away
  useEffect(() => {
    if (sel && !loading && !byYear[sel]) { setSel(null); setPhase(''); }
  }, [sel, loading, byYear]);

  /* ── Admin ── */
  const flash = text => { setToast(text); later(() => setToast(''), 2400); };

  const openForm = () => {
    setMsg('');
    setForm(f => ({ ...f, year: String(sel || f.year) }));
    setShowForm(true);
  };

  const handleSave = async e => {
    e.preventDefault();
    if (!form.pdfUrl.startsWith('http')) { setMsg('Enter a valid URL starting with http'); return; }
    setSaving(true);
    setMsg('');
    try {
      await pyqAPI.create(form);
      setShowForm(false);
      flash('Paper added');
      load();
    } catch (err) {
      setMsg(err.response?.data?.message || 'Failed to add paper');
    } finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Remove this paper?')) return;
    try { await pyqAPI.delete(id); load(); } catch { /* keep the paper */ }
  };

  return (
    <>
    <div className={`cfq ${sel ? 'has-sel' : ''} ${intro ? 'cfq--intro' : ''}`} data-phase={phase || 'idle'}
      style={{ '--art': `url(${skyArt})` }}>
      <PageMeta
        title="UPSC Previous Year Question Papers & PDF Downloads | CivilFox"
        description="Browse available UPSC Civil Services Prelims and Mains previous-year question papers by year and paper. Open the public paper links and download available PDFs."
        path="/pyq"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'UPSC Previous Year Question Papers',
          description: 'Browse available UPSC Civil Services Prelims and Mains question papers by year and paper.',
          url: 'https://civilfox.app/pyq',
        }}
      />
      <h1 className="cfq-title"><span>PYQ Question Papers</span></h1>

      <div className="cfq-corner">
        <button type="button" className="cfq-round" onClick={() => setMuted(m => !m)}
          aria-label={muted ? 'Sound on' : 'Sound off'} aria-pressed={muted}>
          {muted ? <VolumeX size={19} /> : <Volume2 size={19} />}
        </button>
        {isAdmin && (
          <button type="button" className="cfq-round" onClick={openForm} aria-label="Add paper"><Plus size={20} /></button>
        )}
        {sel && (
          <button type="button" className="cfq-round" ref={closeRef} onClick={() => closeBook()} aria-label="Close"><X size={20} /></button>
        )}
      </div>

      <div className="cfq-body">
        {/* The shelf: oldest on the left, newest on the right */}
        <div className="cfq-stage" ref={stageRef} onPointerMove={onMove}>
          <div className="cfq-track" ref={trackRef}>
            {empty ? GHOSTS.map((_, i) => (
              <span key={i} className={`cfq-book cfq-book--ghost ${loading ? 'is-load' : ''}`} style={{ '--i': i }}>
                <span className="cfq-cuboid"><span className="cfq-face cfq-spine" /></span>
              </span>
            )) : shown.map(year => {
              const i = years.indexOf(year);
              const tone = TONES[i % TONES.length];
              return (
                <button key={year} type="button" ref={el => { bookRefs.current[year] = el; }}
                  className={`cfq-book ${sel === year ? 'is-open' : ''}`}
                  style={{ '--i': i, '--bg': tone.bg, '--fg': tone.fg }}
                  aria-label={`${year} papers`} aria-expanded={sel === year}
                  onFocus={onBookFocus} onClick={() => openBook(year)}>
                  <span className="cfq-cuboid">
                    <span className="cfq-face cfq-spine"><b>{year}</b></span>
                    <span className="cfq-face cfq-cover"><i /><b>{year}</b><em>PYQ</em></span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Links land here, one after another */}
        <aside className={`cfq-out ${settled ? 'is-settled' : ''}`} aria-label={sel ? `${sel} papers` : undefined}>
          {sel && (
            <ul className="cfq-stack">
              {list.map((paper, k) => (
                <li key={paper._id} ref={el => { chipRefs.current[k] = el; }}
                  className={`cfq-chip ${k < fired ? 'in' : ''}`} data-type={paper.examType}>
                  <a href={paper.pdfUrl} target="_blank" rel="noreferrer" className="cfq-link">
                    <span className="cfq-code">{paper.paperCode}</span>
                    <span className="cfq-label">
                      {PAPER_LABELS[paper.paperCode] || paper.paperTitle}
                      <small>{paper.examType}{paper.notes ? `, ${paper.notes}` : ''}</small>
                    </span>
                    <span className="cfq-dl" aria-hidden="true"><Download size={18} /></span>
                  </a>
                  {isAdmin && (
                    <button type="button" className="cfq-del" onClick={() => handleDelete(paper._id)} aria-label="Remove paper">
                      <Trash2 size={16} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>

      {toast && <div className="cfq-toast" role="status">{toast}</div>}

      {/* Add paper — admin only */}
      {isAdmin && showForm && (
        <div className="cfq-overlay" onClick={e => { if (e.target === e.currentTarget) setShowForm(false); }}>
          <form className="cfq-sheet" onSubmit={handleSave} role="dialog" aria-modal="true" aria-label="Add paper">
            <div className="cfq-sheet-head">
              <h2>Add paper</h2>
              <button type="button" className="cfq-sheet-x" onClick={() => setShowForm(false)} aria-label="Close"><X size={18} /></button>
            </div>

            <div className="cfq-row3">
              <label>Year
                <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                  {YEARS.map(y => <option key={y}>{y}</option>)}
                </select>
              </label>
              <label>Exam
                <select value={form.examType}
                  onChange={e => { const et = e.target.value; setForm(f => ({ ...f, examType: et, paperCode: et === 'Prelims' ? 'GS-1' : 'GS-I' })); }}>
                  <option>Prelims</option><option>Mains</option>
                </select>
              </label>
              <label>Paper
                <select value={form.paperCode} onChange={e => setForm(f => ({ ...f, paperCode: e.target.value }))}>
                  {(form.examType === 'Prelims' ? PRELIMS_CODES : MAINS_CODES).map(p => (
                    <option key={p} value={p}>{p} — {PAPER_LABELS[p]}</option>
                  ))}
                </select>
              </label>
            </div>
            <label>Title
              <input placeholder="General Studies Paper 1 — 2024" value={form.paperTitle}
                onChange={e => setForm(f => ({ ...f, paperTitle: e.target.value }))} required />
            </label>
            <label>PDF URL
              <input type="url" placeholder="https://upsc.gov.in/sites/default/files/…" value={form.pdfUrl}
                onChange={e => setForm(f => ({ ...f, pdfUrl: e.target.value }))} required />
            </label>
            <label>Notes
              <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </label>

            {msg && <p className="cfq-msg" role="alert">{msg}</p>}
            <button type="submit" className="cfq-save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </form>
        </div>
      )}
    </div>
    <PublicFooter />
    </>
  );
}